import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSession } from '@supabase/auth-helpers-react'; // Added for user data
import api from '../services/api';

// Mock data for charts and feed (replace with API fetches)
const mockMetrics = {
  applications: 45,
  interviews: 28,
  hires: 12,
  timeSaved: '80%', // Or calculate dynamically
};

const mockChartData = [
  { name: 'Jan', applications: 10 },
  { name: 'Feb', applications: 20 },
  { name: 'Mar', applications: 15 },
  { name: 'Apr', applications: 30 },
  { name: 'May', applications: 25 },
  { name: 'Jun', applications: 40 },
];

const mockActivity = [
  { id: 1, message: 'New application for Software Engineer', status: 'new', time: '2 hours ago' },
  { id: 2, message: 'Interview completed for Designer role', status: 'completed', time: 'Yesterday' },
  { id: 3, message: 'Candidate hired for Marketing Lead', status: 'hired', time: '3 days ago' },
  { id: 4, message: 'Job posting closed: Product Manager', status: 'closed', time: '1 week ago' },
];

const EmployerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const session = useSession(); // Get user from session
  const [metrics, setMetrics] = useState(mockMetrics);
  const [chartData, setChartData] = useState(mockChartData);
  const [activity, setActivity] = useState(mockActivity);
  const [userName, setUserName] = useState('User'); // Default 'User'
  const [isSimAdmin, setIsSimAdmin] = useState(false);
  const [loadingAdminCheck, setLoadingAdminCheck] = useState(true);

  useEffect(() => {
    if (!session) {
      navigate('/login'); // Redirect if no session
      return;
    }

    console.log('EmployerDashboard session:', session); // Debug

    // Set userName from metadata or email fallback
    const preferredName = session.user?.app_metadata?.preferredName;
    setUserName(preferredName || session.user?.email?.split('@')[0] || 'User');

    const fetchData = async () => {
      try {
        // Fetch metrics (expand as needed with real endpoints)
        // const metricsResponse = await api.get('/api/dashboard/metrics');
        // setMetrics(metricsResponse.data);
        // Similarly for chartData and activity
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, [session, navigate]);

  useEffect(() => {
    const userId = session?.user?.id;
    setIsSimAdmin(false);
    setLoadingAdminCheck(true);
    if (!userId) return;

    const checkAdmin = async () => {
      try {
        const response = await api.get('/sim/admin/companies');
        setIsSimAdmin(response.status === 200);
      } catch (error: any) {
        setIsSimAdmin(false);
      } finally {
        setLoadingAdminCheck(false);
      }
    };

    checkAdmin();
  }, [session?.user?.id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
  };

  return (
    <div className="min-h-screen bg-surface-page text-txt-primary flex">
      {/* Sidebar Navigation - Fixed, sophisticated with hover states */}
      <aside className="w-64 bg-surface-card/80 backdrop-blur-md border-r border-line/50 flex flex-col p-6 space-y-6">
        <h2 className="text-2xl font-bold tracking-tight">Intellerview</h2>
        <nav className="space-y-2">
          <button
            onClick={() => navigate('/employer/dashboard')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Dashboard
          </button>
          <button
            onClick={() => navigate('/employer/jobs')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Manage Jobs
          </button>
          <button
            onClick={() => navigate('/employer/interviews')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Review Interviews
          </button>
          <button
            onClick={() => navigate('/employer/reports')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Reports
          </button>
          <button
            onClick={() => navigate('/employer/settings')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Settings
          </button>
          <button
            onClick={() => navigate('/employer/sim/results')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Simulations Results
          </button>
          <button
            onClick={() => navigate('/sim/create-link')}
            className="w-full text-left py-2 px-4 rounded-input hover:bg-gray-700/50 transition-colors duration-200 font-medium"
          >
            Simulation Links
          </button>
        </nav>
      </aside>

      {/* Main Content - Flexible, with padding and sections */}
      <main className="flex-1 p-8 space-y-8">
        {/* Header with Quick Actions - Clean, with premium buttons */}
        <header className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Good {getGreeting()}, {userName}
            </h1>
            <p className="text-xl font-light text-txt-secondary mt-1">Here's your recruitment overview for today</p>
          </div>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/employer/create-job')} // Link to JobCreate.tsx
              className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors"
            >
              Create Job
            </button>
            <button
              onClick={() => navigate('/employer/interviews')}
              className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors" // Changed to blue
            >
              Review Interviews
            </button>
            <button
              onClick={() => navigate('/employer/jobs')}
              className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors" // Changed to blue
            >
              Manage Jobs
            </button>
          </div>
        </header>

        {/* Metric Cards - Grid of elegant cards with gradients and glass */}
        {!loadingAdminCheck && isSimAdmin && (
          <section className="bg-surface-card/50 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">Simulation Admin</p>
                <h2 className="text-2xl font-bold tracking-tight mt-2">Admin Dashboard</h2>
                <p className="text-txt-secondary mt-1">Manage imports, variants, credits, links, attempts, and reports.</p>
              </div>
              <Link
                to="/employer/admin"
                className="inline-flex items-center justify-center rounded-input bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-6 py-2 font-semibold transition-colors"
              >
                Open
              </Link>
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-700/80 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
            <h3 className="text-lg font-light text-txt-secondary">Applications</h3>
            <p className="text-4xl font-bold mt-2">{metrics.applications}</p>
            <div className="mt-4 h-2 bg-gray-600 rounded-full">
              <div className="h-full bg-brand-600 rounded-full" style={{ width: '75%' }} /> {/* Progress indicator */}
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-700/80 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
            <h3 className="text-lg font-light text-txt-secondary">Interviews</h3>
            <p className="text-4xl font-bold mt-2">{metrics.interviews}</p>
            <div className="mt-4 h-2 bg-gray-600 rounded-full">
              <div className="h-full bg-brand-600 rounded-full" style={{ width: '60%' }} />
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-700/80 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
            <h3 className="text-lg font-light text-txt-secondary">Hires</h3>
            <p className="text-4xl font-bold mt-2">{metrics.hires}</p>
            <div className="mt-4 h-2 bg-gray-600 rounded-full">
              <div className="h-full bg-purple-500 rounded-full" style={{ width: '40%' }} />
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-700/80 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
            <h3 className="text-lg font-light text-txt-secondary">Time Saved</h3>
            <p className="text-4xl font-bold mt-2">{metrics.timeSaved}</p>
            <div className="mt-4 h-2 bg-gray-600 rounded-full">
              <div className="h-full bg-yellow-500 rounded-full" style={{ width: '80%' }} />
            </div>
          </div>
        </div>

        {/* Data Visualization - Clean chart section */}
        <section className="bg-surface-card/50 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
          <h2 className="text-2xl font-bold mb-4 tracking-tight">Application Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Area type="monotone" dataKey="applications" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </section>

        {/* Activity Feed - Scrollable list with status indicators */}
        <section className="bg-surface-card/50 backdrop-blur-md rounded-card p-6 shadow-xl border border-line/50">
          <h2 className="text-2xl font-bold mb-4 tracking-tight">Recent Activity</h2>
          <ul className="space-y-4 max-h-64 overflow-y-auto">
            {activity.map((item) => (
              <li key={item.id} className="flex justify-between items-center py-2 border-b border-line/50 last:border-b-0">
                <span className="text-txt-secondary">{item.message}</span>
                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    item.status === 'new' ? 'bg-brand-600/20 text-blue-300' :
                    item.status === 'completed' ? 'bg-brand-600/20 text-green-300' :
                    item.status === 'hired' ? 'bg-purple-500/20 text-purple-300' :
                    'bg-gray-500/20 text-txt-secondary'
                  }`}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                  <span className="text-gray-500 text-sm">{item.time}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
};

export default EmployerDashboard;
